import { thumbnailImage } from "~/lib/image-loader";
import { cn, cva, type VariantProps } from "~/lib/utils";

const lockupText = cva({
  base: "truncate font-heading font-semibold",
  variants: {
    size: { sm: "text-base", md: "text-lg", lg: "text-lg lg:text-xl" },
    // "inverse" for dark bands (footer) — P2-DB03 token extension.
    tone: { default: "text-fg", inverse: "text-on-surface-inverse" },
  },
  defaultVariants: { size: "md", tone: "default" },
});

const lockupMarkImg = cva({
  base: "w-auto shrink-0",
  variants: { size: { sm: "h-5", md: "h-6", lg: "h-6 lg:h-8" } },
  defaultVariants: { size: "md" },
});

// Tokenised monogram box (P2-DB03): inverts with the surface it sits on — an
// ink box on the light header, a light box on the dark footer — so the mark
// stays legible on both, and matches the inverse CTA (Subscribe). A brand's
// uploaded mark image renders as-is instead (call site below).
const monogramBox = cva({
  base: "flex shrink-0 items-center justify-center rounded-[7px] font-heading font-semibold leading-none",
  variants: {
    size: {
      sm: "h-6 w-6 text-sm",
      md: "h-6 w-6 text-sm",
      lg: "h-7 w-7 text-base lg:h-8 lg:w-8 lg:text-lg",
    },
    tone: {
      // The mark carries the brand colour on light surfaces (vxolt palette:
      // Pine "V"); on the dark footer it flips to the inverse fill.
      default: "bg-primary text-primary-fg",
      inverse: "bg-on-surface-inverse text-surface-inverse",
    },
  },
  defaultVariants: { size: "md", tone: "default" },
});

/**
 * P2-DB03 · Brand lockup: optional square monogram + wordmark, the identity
 * unit the design repeats across header, footer and drawer. The full `logoUrl`
 * image, where a brand has one, replaces the lockup entirely (precedence at
 * the header call site).
 *
 * The mark renders two ways: an uploaded image (`logoMarkUrl` → a real URL) is
 * shown as-is; a generated mark (a `data:` URI, our placeholder) is redrawn as
 * a tokenised box from the wordmark's initial so it adapts to light vs dark
 * surfaces. A brand with no mark degrades to the wordmark alone.
 */
export function BrandLockup({
  wordmark,
  logoMarkUrl,
  size,
  tone,
  className,
}: {
  wordmark: string;
  logoMarkUrl: string | null;
  className?: string;
} & VariantProps<typeof lockupText>) {
  const uploadedMark = logoMarkUrl && !logoMarkUrl.startsWith("data:");
  const generatedMark = Boolean(logoMarkUrl?.startsWith("data:"));
  const monogram = wordmark.trim().charAt(0).toUpperCase();

  return (
    <span className={cn("flex min-w-0 items-center gap-2", className)}>
      {uploadedMark && (
        // eslint-disable-next-line @next/next/no-img-element
        <img {...thumbnailImage(logoMarkUrl, 32)} alt="" className={lockupMarkImg({ size })} />
      )}
      {!uploadedMark && generatedMark && monogram && (
        <span aria-hidden="true" className={monogramBox({ size, tone })}>
          {monogram}
        </span>
      )}
      <span className={lockupText({ size, tone })}>{wordmark}</span>
    </span>
  );
}
